<template>
  <div :class="$style.container">
    <AuthAdvertisement
      v-if="!isLoading"
      :is-advertisement-loading="isAdvertisementLoading"
      :advertisement="advertisement"
      :image.sync="adImage"
      :has-some-advertisement="hasSomeAdvertisement"
      @click="handleAvertisementAction"
    />
    <div :class="[$style.content]">
      <AuthNavLogo :link="$t('links.host')" />
      <XCard
        :class="$style.cardContainer"
        :padding="cardPadding"
        elevation="low"
        data-testid="signupModal"
      >
        <h4 :class="$style.title">
          {{ $t('auth.userSignup.title') }}
        </h4>
        <div
          v-if="showSsoCardButtons"
          data-testid="loginWithEmailCard"
        >
          <AuthSsoOptions
            v-if="!isLoading"
            :oauth-links="initialLinks.oauth2"
            :visible-buttons="ssoVisibleButtons"
            button-design="outlined"
            data-testid="ssoOptions"
            vertical-align
          />
          <AuthSeparatorLine
            :class="$style.separator"
            :text="$t('auth.userSignup.form.orEnter')"
          />
          <XButton
            full-width
            design="outlined"
            data-testid="loginWithEmailButton"
            @click="handleRegisterWithEmail"
          >
            {{ $t('auth.userSignup.withEmail') }}
          </XButton>
        </div>
        <div
          v-else
          :class="$style.formContent"
        >
          <XTextInput
            v-model="$v.signUpData.name.$model"
            :class="$style.field"
            :label="$t('auth.userSignup.form.name.label')"
            :validations="dynamicValidators.name"
            :message="signUpRequestErrors.name"
            data-testid="nameField"
            full-width
            @input="resetErrorMessage('name', $event)"
            @blur="handleXTextInputBlur('name', 'signUpData')"
          />
          <XTextInput
            v-model="$v.signUpData.email.$model"
            :class="$style.field"
            :label="$t('auth.userSignup.form.email.label')"
            :validations="dynamicValidators.email"
            data-testid="emailField"
            :message="signUpRequestErrors.email"
            full-width
            @input="resetErrorMessage('email', $event)"
            @blur="handleXTextInputBlur('email', 'signUpData')"
          />

          <AuthPasswordField
            :class="$style.field"
            :label="$t('auth.userSignup.form.password.label')"
            show-requirements
            :forbidden-words="signUpData.name"
            :value="signUpData.password"
            @input="(password, validation) => handlePassword(password, validation)"
          />

          <i18n
            path="auth.userSignup.form.accessTermEnabled.text"
            tag="span"
            :class="$style.checkboxText"
          >
            <XLink
              target="_blank"
              :to="$t('links.legal.useTerms')"
              no-padding
              small
            >
              {{ $t('auth.userSignup.form.accessTermEnabled.useTerms') }}
            </XLink>
            <XLink
              target="_blank"
              :to="$t('links.legal.privacyPolicy')"
              no-padding
              small
            >
              {{ $t('auth.userSignup.form.accessTermEnabled.privacity') }}
            </XLink>
          </i18n>

          <XButton
            :class="$style.submitButton"
            type="submit"
            :disabled="!canSubmit"
            :loading="isSubmiting"
            data-testid="submitButton"
            @click="handleSubmit"
          >
            {{ $t('auth.userSignup.form.button') }}
          </XButton>
        </div>
      </XCard>
      <div :class="$style.login">
        <span> {{ $t('auth.userSignup.login.text') }} </span>
        <XLink
          :to="'/signin'"
          data-testid="loginLink"
        >
          {{ $t('auth.userSignup.login.link') }}
        </XLink>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref, onMounted } from 'vue';
import { useAuthAdvertisement } from '@auth/composables';
import { mapGetters } from 'vuex';
import { required, email, fullName } from '@base/validators';
import { xTextInputMixin } from '@base/mixins';
import { useBreakpoints, useGA, useRouter } from '@base/hooks';
import AuthNavLogo from '@auth/components/AuthNavLogo/AuthNavLogo.vue';
import AuthAdvertisement from '@auth/components/AuthAdvertisement/AuthAdvertisement.vue';
import AuthSeparatorLine from '@auth/components/AuthSeparatorLine/AuthSeparatorLine.vue';
import AuthSsoOptions from '@auth/components/AuthSsoOptions/AuthSsoOptions.vue';
import AuthPasswordField from '@auth/components/AuthPasswordField/AuthPasswordField.vue';
import { triggerDataLayerEvent } from '@base/utils/data-layer-utils';
import { getCookie } from '@base/utils/cookies';
import { sentry } from '@/plugins/sentry';
import { setExpressSubscription } from '@account/utils/express-subscription';
import { useStorage } from '@vueuse/core';
import { useStore } from '@/store';
import axios from 'axios';

export default defineComponent({
  name: 'AuthRegisterView',
  components: {
    AuthNavLogo,
    AuthSeparatorLine,
    AuthSsoOptions,
    AuthAdvertisement,
    AuthPasswordField,
  },
  mixins: [xTextInputMixin],
  setup() {
    const advertisementName = 'register';
    const {
      isAdvertisementLoading,
      advertisement,
      hasSomeAdvertisement,
      adImage,
      handleAvertisementClick,
      fetchAdvertisement,
    } = useAuthAdvertisement(advertisementName);

    const initialLinks = ref({});
    const autofillTermsOptin = ref(false);
    const isLoading = ref(true);

    const store = useStore();

    const signUpData = ref({
      name: '',
      email: '',
      password: '',
      accessTermEnabled: true,
    });

    const router = useRouter();

    const fetchInitialData = async () => {
      try {
        const { data } = await store.dispatch('auth/fetchSignupData');

        initialLinks.value = data.links;

        store.commit('auth/SET_LINKS', data.links);

        signUpData.value.email = data.autofillEmail;
        if (data.invitedEmail) signUpData.value.email = data.invitedEmail;
        signUpData.value.name = data.autofillName;
        autofillTermsOptin.value = data.autofillTermsOptin;
      } catch (err) {
        if (axios.isAxiosError(err)) router.push('/500');
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(async () => {
      await fetchInitialData();
      fetchAdvertisement();
    });

    const { breakpoints } = useBreakpoints();

    const cardPadding = computed(() =>
      breakpoints.value.extraSmall
        ? 'var(--space-medium) var(--space-regular)'
        : 'var(--space-large-x) var(--space-medium)'
    );

    const handleAvertisementAction = () => {
      handleAvertisementClick();
    };

    return {
      advertisement,
      adImage,
      hasSomeAdvertisement,
      handleAvertisementAction,
      cardPadding,
      isAdvertisementLoading,
      initialLinks,
      autofillTermsOptin,
      isLoading,
      signUpData,
    };
  },
  data() {
    return {
      showSsoCardButtons: true,
      passwordStrengthText: '',
      isSubmiting: false,
      isValidPassword: false,
      signUpRequestErrors: {
        name: '',
        email: '',
      },
      ssoVisibleButtons: ['google', 'microsoft'],
    };
  },
  computed: {
    ...mapGetters({ links: 'auth/getLinks' }),
    ...mapGetters({ user: 'auth/getUser' }),
    ...mapGetters({ toggles: 'info/getToggles' }),
    canSubmit() {
      return !this.$v.$invalid && this.signUpData.accessTermEnabled && this.isValidPassword;
    },
  },
  created() {
    triggerDataLayerEvent('initSignupView');
    this.appendTrackingAnalytics();
    this.captureExpressSubscription();
    this.verifyIntendedPlan();
  },
  methods: {
    async handleSubmit() {
      this.isSubmiting = true;
      try {
        await this.$store.dispatch('auth/signup', this.signUpData);
        this.handleRequestSuccess();
      } catch (err) {
        this.isSubmiting = false;
        this.handleRequestError(err);
      }
    },
    handleRequestSuccess() {
      triggerDataLayerEvent('signupSuccess');
      triggerDataLayerEvent('new-user', {
        email: this.signUpData?.email,
        user_id: this.user.id,
      });
      window.location = this.links.nextStep;
    },
    handleRequestError(err) {
      this.signUpData.password = '';
      const errors = _.get(err, 'response.data.errors', []);
      if (!Object.keys(errors).length) return;
      const errorDataProperties = _.mapValues(errors, (messages = []) => messages[0] || '');
      this.signUpRequestErrors = { ...errorDataProperties };
    },
    resetErrorMessage(field, value) {
      if (!value) this.$set(this.signUpRequestErrors, field, '');
    },
    handlePassword(password, validation) {
      this.signUpData.password = password;
      this.isValidPassword = validation;
    },
    appendTrackingAnalytics() {
      try {
        const searchParams = new URLSearchParams(window.location.search);

        this.signUpData.userAnalyticsTrackingAttributes = {
          data: {
            ga: getCookie('_ga'),
            utmSource: searchParams.get('utm_source'),
            utmMedium: searchParams.get('utm_medium'),
            utmCampaign: searchParams.get('utm_campaign'),
            utmContent: searchParams.get('utm_content'),
            utmTerm: searchParams.get('utm_term'),
            gclid: searchParams.get('gclid'),
          },
        };
      } catch (error) {
        sentry.captureException(error);
      }
    },
    captureExpressSubscription() {
      try {
        const searchParams = new URLSearchParams(window.location.search);
        const subscription = searchParams.get('subscribe');
        if (!subscription) return;
        const plan = searchParams.get('plan');
        const planId = searchParams.get('plan_id');
        setExpressSubscription({
          subscription: subscription === 'true',
          plan,
          planId,
        });
      } catch (error) {
        sentry.captureException(error);
      }
    },
    verifyIntendedPlan() {
      const searchParams = new URLSearchParams(window.location.search);
      const intendedPlan = searchParams.get('intended_plan');
      if (!intendedPlan) return;
      const intendedPlanStorage = useStorage('intendedPlan', null, sessionStorage);
      intendedPlanStorage.value = intendedPlan;
    },
    handleRegisterWithEmail() {
      useGA('event', 'signup-with-email-click', 'signup-app');
      this.showSsoCardButtons = false;
    },
  },
  validations: {
    signUpData: {
      name: {
        required,
        fullName,
      },
      email: {
        email,
        required,
      },
      accessTermEnabled: {
        required,
      },
    },
  },
});
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: row;
  min-height: 100vh;

  .content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    width: 100%;
    margin: 0px;

    .cardContainer {
      width: calc(100vw - var(--space-large));
      max-width: 496px;

      .title {
        width: 100%;
        max-width: 308px;
        margin: 0 auto var(--space-regular);
        text-align: center;

        @include breakpoint('extra-small') {
          padding-bottom: 0;
        }
      }

      .separator {
        margin: var(--space-regular) 0;
      }

      .formContent {
        .field {
          margin-bottom: var(--space-regular);
        }

        input::-ms-reveal,
        input::-ms-clear {
          display: none;
        }

        .checkboxText {
          font-size: var(--font-size-small);
        }

        .submitButton {
          display: flex;
          width: 100%;
          max-width: 118px;
          margin: var(--space-large) auto 0;

          @include breakpoint('extra-small') {
            max-width: unset;
          }
        }
      }
    }

    .login {
      display: flex;
      align-items: center;
      flex-direction: row;
      margin: var(--space-large-x) var(--space-small-xx) var(--space-medium);
      gap: var(--space-small-xxx);
      color: var(--color-neutral-800);

      @include breakpoint('extra-small') {
        display: flex;
        flex-direction: column;
        text-align: center;
      }
    }
  }
}

@include breakpoint('medium') {
  * {
    box-sizing: border-box;
  }

  .container {
    display: flex;

    .content {
      justify-content: center;
      overflow-y: scroll;
      height: 100vh;
      padding: 0;
    }
  }
}
</style>
